/**
 * 1.4.1: 成员配额进度条组件
 * 显示组织成员使用情况（已使用/总配额）
 * 遵循 YUI 主题设计规范
 */

import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MemberQuotaBarProps {
  currentMembers: number;
  maxMembers: number;
  showAdjustButton?: boolean;
  onAdjust?: () => void;
}

/**
 * 计算使用百分比
 */
const getUsagePercent = (used: number, max: number): number => {
  if (max <= 0) return 0;
  return Math.round((used / max) * 100);
};

/**
 * 获取进度条颜色
 * - 正常：紫色 (primary)
 * - 70%以上：橙色
 * - 90%以上：红色
 */
const getBarColor = (percent: number): string => {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-orange-500';
  return 'bg-primary';
};

export function MemberQuotaBar({
  currentMembers,
  maxMembers,
  showAdjustButton = false,
  onAdjust,
}: MemberQuotaBarProps) {
  const { t } = useTranslation();
  const percent = getUsagePercent(currentMembers, maxMembers);
  const barColor = getBarColor(percent);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Users className="w-4 h-4 text-primary" />
          </div>
          <span className="text-sm font-medium text-gray-700">
            {t('memberQuota', '成员配额')}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-900 font-semibold">
            {currentMembers}/{maxMembers} {t('seatsUsed', '席位已使用')}
          </span>
          
          {showAdjustButton && onAdjust && (
            <button
              onClick={onAdjust}
              className="text-xs text-primary hover:text-primary-dark flex items-center gap-1 px-2 py-1 hover:bg-primary/5 rounded-lg transition-colors"
            >
              <Users className="w-3 h-3" />
              {t('adjustSeats', '调整人数')}
            </button>
          )}
        </div>
      </div>
      
      {/* 进度条 */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      
      {/* 1.5.2: 配额警告提示 - 区分用尽和即将用尽 */}
      {percent >= 100 && (
        <p className="text-xs text-red-600 mt-2">
          {t('quotaFull', '成员配额用尽，如要追加用户，请额外购买席位')}
        </p>
      )}
      {percent >= 90 && percent < 100 && (
        <p className="text-xs text-orange-600 mt-2">
          {t('quotaAlmostFull', '配额即将用尽，请考虑升级套餐')}
        </p>
      )}
    </div>
  );
}
